/**
 * M23 Cards Gallery — horizontal feed + lightbox.
 * Feed: mouse drag-to-scroll (touch/pen keep native overflow).
 * Lightbox: pointer drag on the stage to change slides.
 */
(function () {
  var DRAG_THRESHOLD = 6;
  var SLIDE_DRAG = 48;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isOverflowing(track) {
    return track.scrollWidth > track.clientWidth + 1;
  }

  function initGallery(root) {
    if (!root || root.dataset.cardsGalleryBound === 'true') return;

    var lightbox = root.querySelector('[data-cards-gallery-lightbox]');
    if (!lightbox || typeof lightbox.showModal !== 'function') return;

    var track = root.querySelector('[data-cards-gallery-track]');
    var stage = lightbox.querySelector('[data-cards-gallery-stage]');
    var opens = Array.prototype.slice.call(root.querySelectorAll('[data-cards-gallery-open]'));
    var slides = Array.prototype.slice.call(lightbox.querySelectorAll('[data-cards-gallery-slide]'));
    var count = slides.length;
    if (!opens.length || !count) return;

    root.dataset.cardsGalleryBound = 'true';

    var captionEl = lightbox.querySelector('[data-cards-gallery-caption]');
    var counterEl = lightbox.querySelector('[data-cards-gallery-counter]');
    var counterTemplate =
      (counterEl && counterEl.getAttribute('data-counter-template')) || '__INDEX__ / __COUNT__';
    var closeBtn = lightbox.querySelector('[data-cards-gallery-close]');
    var prevBtn = lightbox.querySelector('[data-cards-gallery-prev]');
    var nextBtn = lightbox.querySelector('[data-cards-gallery-next]');
    var index = 0;
    var lastOpener = null;

    function wrap(i) {
      return ((i % count) + count) % count;
    }

    function updateMeta() {
      var slide = slides[index];
      var caption = (slide && slide.getAttribute('data-caption')) || '';
      if (captionEl) {
        if (caption) {
          captionEl.textContent = caption;
          captionEl.hidden = false;
        } else {
          captionEl.textContent = '';
          captionEl.hidden = true;
        }
      }
      if (counterEl) {
        counterEl.textContent = counterTemplate
          .replace('__INDEX__', String(index + 1))
          .replace('__COUNT__', String(count));
      }
    }

    function setSlide(nextIndex) {
      index = wrap(nextIndex);
      var prevIndex = wrap(index - 1);
      var nextIndexVisible = wrap(index + 1);

      slides.forEach(function (slide, i) {
        slide.classList.remove('is-current', 'is-prev', 'is-next');
        slide.hidden = true;
        if (i === index) {
          slide.classList.add('is-current');
          slide.hidden = false;
        } else if (count > 2 && i === prevIndex) {
          slide.classList.add('is-prev');
          slide.hidden = false;
        } else if (count > 1 && i === nextIndexVisible) {
          slide.classList.add('is-next');
          slide.hidden = false;
        } else if (count === 2 && i === prevIndex) {
          slide.classList.add('is-prev');
          slide.hidden = false;
        }
      });

      updateMeta();
      if (prevBtn) prevBtn.disabled = count < 2;
      if (nextBtn) nextBtn.disabled = count < 2;
    }

    function openAt(i, opener) {
      lastOpener = opener || null;
      setSlide(i);
      if (!lightbox.open) lightbox.showModal();
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (lightbox.open) lightbox.close();
    }

    function onClosed() {
      if (lastOpener && typeof lastOpener.focus === 'function') {
        lastOpener.focus();
      }
      lastOpener = null;
    }

    function bindTrackDrag() {
      if (!track) return;

      var pointerId = null;
      var startX = 0;
      var startScroll = 0;
      var dragged = false;
      var suppressClick = false;

      function onPointerMove(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var dx = event.clientX - startX;
        if (!dragged) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          dragged = true;
          track.classList.add('is-dragging');
          if (typeof track.setPointerCapture === 'function') {
            track.setPointerCapture(pointerId);
          }
        }
        track.scrollLeft = startScroll - dx;
      }

      function endDrag(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var wasDragged = dragged;
        if (
          typeof track.hasPointerCapture === 'function' &&
          track.hasPointerCapture(pointerId)
        ) {
          track.releasePointerCapture(pointerId);
        }
        pointerId = null;
        dragged = false;
        track.classList.remove('is-dragging');
        if (wasDragged) {
          suppressClick = true;
          window.setTimeout(function () {
            suppressClick = false;
          }, 0);
        }
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', endDrag);
        track.removeEventListener('pointercancel', endDrag);
      }

      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (!isOverflowing(track)) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        dragged = false;
        track.addEventListener('pointermove', onPointerMove);
        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);
      });

      track.addEventListener(
        'click',
        function (event) {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      track.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
    }

    function bindStageDrag() {
      if (!stage || count < 2) return;

      var pointerId = null;
      var startX = 0;
      var dragged = false;
      var lastDx = 0;
      var suppressClick = false;

      function isChrome(target) {
        return !!(
          target &&
          target.closest &&
          target.closest(
            '[data-cards-gallery-close], [data-cards-gallery-prev], [data-cards-gallery-next]'
          )
        );
      }

      function clearDrag() {
        stage.classList.remove('is-dragging');
        stage.style.removeProperty('--gallery-drag');
      }

      function onPointerMove(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var dx = event.clientX - startX;
        lastDx = dx;
        if (!dragged) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          dragged = true;
          stage.classList.add('is-dragging');
          if (typeof stage.setPointerCapture === 'function') {
            stage.setPointerCapture(pointerId);
          }
        }
        event.preventDefault();
        if (!prefersReducedMotion()) {
          stage.style.setProperty('--gallery-drag', dx + 'px');
        }
      }

      function endDrag(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var wasDragged = dragged;
        var dx = lastDx;
        if (
          typeof stage.hasPointerCapture === 'function' &&
          stage.hasPointerCapture(pointerId)
        ) {
          stage.releasePointerCapture(pointerId);
        }
        pointerId = null;
        dragged = false;
        lastDx = 0;
        clearDrag();
        if (wasDragged) {
          suppressClick = true;
          window.setTimeout(function () {
            suppressClick = false;
          }, 0);
          if (dx <= -SLIDE_DRAG) {
            setSlide(index + 1);
          } else if (dx >= SLIDE_DRAG) {
            setSlide(index - 1);
          }
        }
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('pointerup', endDrag);
        stage.removeEventListener('pointercancel', endDrag);
      }

      stage.addEventListener('pointerdown', function (event) {
        if (event.button !== 0) return;
        if (isChrome(event.target)) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        lastDx = 0;
        dragged = false;
        stage.addEventListener('pointermove', onPointerMove);
        stage.addEventListener('pointerup', endDrag);
        stage.addEventListener('pointercancel', endDrag);
      });

      stage.addEventListener(
        'click',
        function (event) {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      stage.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
    }

    opens.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-index'), 10);
        if (isNaN(i)) i = 0;
        openAt(i, btn);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        setSlide(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        setSlide(index + 1);
      });
    }

    slides.forEach(function (slide) {
      slide.addEventListener('click', function () {
        if (slide.classList.contains('is-prev')) {
          setSlide(index - 1);
        } else if (slide.classList.contains('is-next')) {
          setSlide(index + 1);
        }
      });
    });

    lightbox.addEventListener('keydown', function (event) {
      if (!lightbox.open) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setSlide(index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setSlide(index + 1);
      }
    });

    lightbox.addEventListener('close', onClosed);

    bindTrackDrag();
    bindStageDrag();
  }

  function initAll() {
    document.querySelectorAll('[data-cards-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    var root = section.querySelector('[data-cards-gallery]');
    if (root) initGallery(root);
  });
})();
